import type { HTMLAttributes } from 'react';
import { clsx } from '../clsx';
import styles from './Progress.module.css';

export interface ProgressProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** 0–100. */
  value: number;
  /** Accessible name, e.g. "Postfach-Import". */
  label: string;
  /** `default` is the 6 px bar of the settings (sunken track with inset well); `thin` the 5 px upload bar; `inverse` the 4 px bar on navy. */
  variant?: 'default' | 'thin' | 'inverse' | undefined;
  /** Shows "64 %" right of the bar (tabular). */
  showValue?: boolean | undefined;
}

/** Determinate progress bar; the fill is `--accent` on every surface. */
export function Progress({
  value,
  label,
  variant = 'default',
  showValue = false,
  className,
  ...rest
}: ProgressProps) {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div className={clsx(styles.wrap, className)} {...rest}>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={clamped}
        className={clsx(styles.track, styles[variant])}
      >
        <div className={styles.fill} style={{ width: `${clamped}%` }} />
      </div>
      {showValue ? (
        <span className={clsx(styles.value, variant === 'inverse' && styles.valueInverse)}>
          {clamped}&nbsp;%
        </span>
      ) : null}
    </div>
  );
}
